import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Row, Col, Alert } from 'react-bootstrap';
import {
  getCreditOverview,
  openCreditTopUpModal,
  creditPurchasesPath,
  creditPurchaseDetailPath,
  CREDIT_WALLET_UPDATED_EVENT,
} from '../../services/CreditService';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import CommonTable from '../components/CommonTable/CommonTable';
import '../tasks/tasklist.css';
import '../users/userlist.css';
import './CreditWalletPage.css';

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toFixed(2);
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const BATCH_COLUMNS = [
  { key: 'ref', header: 'Ref', accessor: (row) => `#${row.ref_id}` },
  {
    key: 'source',
    header: 'Source',
    accessor: (row) => row.source,
    cellClassName: 'text-capitalize',
  },
  {
    key: 'balance',
    header: 'Balance',
    accessor: (row) => `$${formatMoney(row.balance)}`,
  },
  {
    key: 'validity',
    header: 'Valid until',
    accessor: (row) => formatDate(row.validity_date),
  },
];

const PURCHASE_COLUMNS = [
  {
    key: 'date',
    header: 'Date',
    accessor: (row) => formatDate(row.purchase_date),
  },
  {
    key: 'source',
    header: 'Source',
    accessor: (row) => row.source,
    cellClassName: 'text-capitalize',
  },
  {
    key: 'purchased',
    header: 'Purchased',
    accessor: (row) => `$${formatMoney(row.amount_purchased_usd)}`,
  },
  {
    key: 'balance',
    header: 'Balance',
    accessor: (row) => `$${formatMoney(row.balance)}`,
  },
  {
    key: 'validity',
    header: 'Valid until',
    accessor: (row) => formatDate(row.validity_date),
  },
];

export default function CreditWalletPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getCreditOverview();
      if (res?.success) {
        setData(res);
      } else {
        setError(res?.message || 'Failed to load credits');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load credits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onUpdate = () => load();
    window.addEventListener(CREDIT_WALLET_UPDATED_EVENT, onUpdate);
    return () => window.removeEventListener(CREDIT_WALLET_UPDATED_EVENT, onUpdate);
  }, [load]);

  const balance = data?.credits?.total_balance ?? 0;
  const batches = data?.credits?.batches || [];
  const recentPurchases = data?.recent_purchases || [];

  return (
    <div className="credit-wallet-page">
      <Row className="credit-wallet-row">
        <Col lg={12}>
          <div className="user-list-card credit-wallet-main-card">
            <div className="credit-wallet-content">
              {loading && (
                <div className="loading-container credit-wallet-loading">
                  <div className="loading-spinner" />
                </div>
              )}

              {!loading && error && (
                <Alert variant="danger" className="mb-0">
                  {error}
                </Alert>
              )}

              {!loading && !error && data && (
                <>
                  <section className="credit-wallet-block credit-wallet-balance-block">
                    <div className="credit-wallet-balance-card">
                      <div className="credit-wallet-balance-icon" aria-hidden="true">
                        <i className="fas fa-wallet" />
                      </div>
                      <div className="credit-wallet-balance-meta">
                        <span className="credit-wallet-balance-label">Available balance</span>
                        <span className="credit-wallet-balance-value">${formatMoney(balance)}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="new-user-btn credit-wallet-topup-btn"
                      onClick={() => openCreditTopUpModal()}
                    >
                      <i className="fas fa-plus me-2" aria-hidden="true" />
                      Top up credits
                    </button>
                  </section>

                  <section className="credit-wallet-block">
                    <h6 className="credit-wallet-section-title">Active batches</h6>
                    <div className="row g-0 credit-wallet-table-row">
                      <CommonTable
                        columns={BATCH_COLUMNS}
                        data={batches}
                        onRowClick={(row) => navigate(creditPurchaseDetailPath(row.ref_id))}
                        emptyMessage="No active credit batches"
                        emptyStateIcon="fas fa-layer-group"
                        getRowId={(row) => row.ref_id}
                        minHeight="0"
                        maxHeight="none"
                        className="credit-wallet-common-table"
                      />
                    </div>
                  </section>

                  <section className="credit-wallet-block">
                    <div className="credit-wallet-section-header">
                      <h6 className="credit-wallet-section-title mb-0">Recent purchases</h6>
                      <Link to={creditPurchasesPath()} className="credit-wallet-view-all-link">
                        View all
                      </Link>
                    </div>
                    <div className="row g-0 credit-wallet-table-row">
                      <CommonTable
                        columns={PURCHASE_COLUMNS}
                        data={recentPurchases}
                        onRowClick={(row) => navigate(creditPurchaseDetailPath(row.ref_id))}
                        emptyMessage="No purchases yet"
                        emptyStateIcon="fas fa-receipt"
                        getRowId={(row) => row.ref_id}
                        minHeight="0"
                        maxHeight="none"
                        className="credit-wallet-common-table"
                      />
                    </div>
                  </section>
                </>
              )}
            </div>
          </div>
        </Col>
      </Row>

      <InternalFooter />
    </div>
  );
}
